import React from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schema";
import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { GAMES_BY_TIME } from "../data/games";
import { useNowIST } from "../lib/useResults";

const PATH = "/about-us";

const About = () => {
  const nowIST = useNowIST();
  const currentYear = nowIST.getFullYear();

  const title = "About Bagad Satta Company — Who We Are & How Results Are Published";
  const description =
    "Bagad Satta Company publishes free daily satta results and permanent monthly charts for ten games. Learn how numbers are sourced, when each game is declared, and what this site is not.";

  const trail = [
    { name: "Home", path: "/" },
    { name: "About Us", path: PATH },
  ];

  return (
    <>
      <Seo
        path={PATH}
        title={title}
        description={description}
        keywords="about bagad satta company, satta result website, satta chart archive"
        schema={[
          organizationSchema(),
          webPageSchema({ path: PATH, title, description }),
          breadcrumbSchema(trail),
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <Breadcrumbs trail={trail} />

          <PageHeader
            h1="About Bagad Satta Company"
            intro="A free, fast and permanent public record of daily game results."
            nowIST={nowIST}
          />

          <section className="card text-content">
            <h2>What This Website Does</h2>
            <p>
              Bagad Satta Company does one thing: it shows the declared number
              for ten games, as soon as each is declared, and it keeps that
              number on record afterwards. There is no account to create, no
              paywall and no limit on how much of the archive you can read.
            </p>

            <h2>How Results Reach This Page</h2>
            <p>
              Each game has a fixed declaration time in IST. Our result data is
              held in a maintained dataset per game and per year, and the page
              reads from it directly in your browser. Before a game&apos;s
              scheduled time has passed, that day&apos;s cell deliberately shows{" "}
              <strong>XX</strong> rather than a number — we would rather show
              nothing than show something unconfirmed.
            </p>

            <h2>Games We Cover</h2>
            <ul className="related-links">
              {GAMES_BY_TIME.map((game) => (
                <li key={game.key}>
                  <Link to={`/${game.slug}`}>
                    {game.name} — declared at {game.timeLabel} IST
                  </Link>
                </li>
              ))}
            </ul>

            <h2>Our Archive</h2>
            <p>
              We hold complete monthly records for 2024, 2025 and {currentYear}.
              Old months are never removed. You can open any of them from the{" "}
              <Link to="/satta-chart">satta chart page</Link>.
            </p>

            <h2>What This Website Is Not</h2>
            <p>
              We are not a satta matka company, a bookmaker, an agent or a
              gambling operator, and we are not affiliated with any. We publish
              information. Satta and gambling are illegal in many Indian states
              and in many countries — please read our{" "}
              <Link to="/disclaimer">disclaimer</Link> before using anything on
              this site.
            </p>

            <h2>Contact</h2>
            <p>
              Corrections, questions and takedown requests are all welcome via
              the <Link to="/contact-us">contact page</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default About;
